import React, { useCallback, useEffect, useState } from 'react';
import { Button, message } from 'antd';
import { usePage } from '@inertiajs/react';
import api from '../../../lib/api';
import useElapsedSeconds from '../../../hooks/useElapsedSeconds';
import type { SharedProps } from '../../../types';
import type { TimeEntry } from '../../../types/timeEntry';

export const TIMER_CHANGED_EVENT = 'tracker:timer-changed';

export function formatClock(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  return [hours, minutes, rest].map((part) => String(part).padStart(2, '0')).join(':');
}

export function notifyTimerChanged(): void {
  window.dispatchEvent(new Event(TIMER_CHANGED_EVENT));
}

const TimerBar: React.FC = () => {
  const loggedIn = Boolean(usePage<SharedProps>().props.auth?.user);
  const [entry, setEntry] = useState<TimeEntry | null>(null);
  const [stopping, setStopping] = useState(false);
  const elapsed = useElapsedSeconds(entry?.started_at ?? null);

  const refresh = useCallback(async () => {
    try {
      const current = (await api.timeEntries.current()) as TimeEntry | null;
      setEntry(current);
    } catch {
      setEntry(null);
    }
  }, []);

  useEffect(() => {
    // A guest has no timer. Without this, every public page (and every window
    // focus on one) sent a request that could only answer 401.
    if (!loggedIn) {
      setEntry(null);
      return;
    }

    refresh();

    window.addEventListener(TIMER_CHANGED_EVENT, refresh);
    // 从后台标签页切回来时对一次表，免得显示的秒数是休眠前的
    window.addEventListener('focus', refresh);

    return () => {
      window.removeEventListener(TIMER_CHANGED_EVENT, refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [loggedIn, refresh]);

  const stop = async () => {
    setStopping(true);
    try {
      await api.timeEntries.stop();
      setEntry(null);
      notifyTimerChanged();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '停止失败');
    } finally {
      setStopping(false);
    }
  };

  if (!entry) return null;

  return (
    <div className="sticky top-0 z-20 border-b border-purple-200 bg-purple-50/90 backdrop-blur">
      <div className="container mx-auto flex items-center gap-3 px-4 py-2">
        <span className="h-2 w-2 flex-none animate-pulse rounded-full bg-purple-500" />

        <div className="min-w-0 flex-1 text-sm">
          <span className="font-medium text-brand-ink">
            {entry.task_title ?? entry.project_name ?? '未归类计时'}
          </span>
          {entry.task_title && entry.project_name && (
            <span className="ml-2 text-brand-ink3">· {entry.project_name}</span>
          )}
          {entry.note && <span className="ml-2 text-brand-ink3">{entry.note}</span>}
        </div>

        <span className="flex-none font-mono text-lg tabular-nums text-brand-ink">
          {formatClock(elapsed)}
        </span>

        <Button size="small" loading={stopping} onClick={stop}>
          停止
        </Button>
      </div>
    </div>
  );
};

export default TimerBar;
